import Papa from 'papaparse'
import React from 'react'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { pdfjs } from 'react-pdf'

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js`

//storageUtils

export const convertStorage = (size: any): string => {
  if (size >= 1024 * 1024 * 1024 * 1024) {
    return `${(size / (1024 * 1024 * 1024 * 1024)).toFixed(2)} TB`
  } else if (size >= 1024 * 1024 * 1024) {
    return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`
  } else if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(2)} MB`
  } else if (size >= 1024) {
    return `${(size / 1024).toFixed(2)} KB`
  } else {
    return `${size} B`
  }
}

// src/utils/fileUtils.ts

export const parseFileInfoFromUrl = (url: string) => {
  if (!url) return { type: '', name: '', size: '' }

  const typeMatch = url.match(/type-(.*?)-originalname-/)
  const nameMatch = url.match(/originalname-(.*?)-size-/)
  const sizeMatch = url.match(/size-(\d+)/)

  return {
    type: typeMatch ? typeMatch[1] : '',
    name: nameMatch ? decodeURIComponent(nameMatch[1]) : 'file',
    size: sizeMatch ? sizeMatch[1] : ''
  }
}

// utils/filePreviewUtils.tsx

export const renderFilePreview = (
  previewFile: string | null,
  onLoad: () => void,
  fileContent?: string
) => {
  if (!previewFile) return null

  const lowerFile = previewFile.toLowerCase()
  const fileType = lowerFile.split('.').pop() || ''

  const isPDF = lowerFile.includes('.pdf')
  const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(lowerFile)
  const isVideo = /\.(mp4|webm|mov)$/i.test(lowerFile)
  const isText = /\.(txt|log|md|xml)$/i.test(lowerFile)
  const isCode = /\.(json|ts|tsx|js|jsx|py|java|html|css|sh|yml|yaml)$/i.test(
    lowerFile
  )
  const isOffice = /\.(doc|docx|xls|xlsx|ppt|pptx)$/i.test(lowerFile)
  const isCSV = /\.csv$/i.test(lowerFile)

  if (isCSV && fileContent) {
    const parsed = Papa.parse(fileContent.trim(), { header: false }).data
    return (
      <div className='overflow-auto max-h-[90vh]'>
        <table className='min-w-full table-auto border border-gray-300'>
          <tbody>
            {parsed.map((row: any, rowIndex: number) => (
              <tr key={rowIndex}>
                {row.map((cell: any, colIndex: number) => (
                  <td
                    key={colIndex}
                    className='border border-gray-300 px-3 py-1 text-sm text-white text-left whitespace-nowrap'
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  if ((isText || isCode) && !isCSV) {
    return fileContent ? (
      <div className='overflow-y-auto max-h-[90vh]'>
        <SyntaxHighlighter
          language={fileType}
          style={{
            hljs: {
              background: '#000000',
              color: '#ffffff',
              padding: '1rem'
            },
            keyword: { color: '#FF79C6' },
            string: { color: '#F1FA8C' },
            function: { color: '#8BE9FD' },
            title: { color: '#50FA7B' },
            comment: { color: '#6272A4', fontStyle: 'italic' },
            number: { color: '#BD93F9' },
            meta: { color: '#FFB86C' }
          }}
          wrapLongLines
          customStyle={{
            fontSize: '14px',
            backgroundColor: '#000000',
            margin: 0,
            borderRadius: 0,
            boxShadow: 'none',
            lineHeight: '1.5'
          }}
          showLineNumbers
        >
          {fileContent}
        </SyntaxHighlighter>
      </div>
    ) : (
      <div className='text-center text-gray-500 p-4'>
        Loading file content...
      </div>
    )
  }

  if (isImage) {
    return (
      <div className='flex justify-center items-center'>
        <img
          src={previewFile}
          alt='preview'
          className='max-h-[90vh] object-contain'
          onLoad={onLoad}
        />
      </div>
    )
  }

  if (isVideo) {
    return (
      <div className='flex justify-center'>
        <video
          controls
          className='max-w-full max-h-[90vh]'
          onLoadedData={onLoad}
        >
          <source src={previewFile} type={`video/${fileType}`} />
          Your browser does not support the video tag.
        </video>
      </div>
    )
  }

  if (isPDF) {
    return (
      <iframe
        src={`https://docs.google.com/viewer?url=${encodeURIComponent(previewFile)}&embedded=true`}
        style={{ width: '100%', height: '90vh', border: 'none' }}
        title='Google Docs PDF Preview'
        onLoad={onLoad}
      />
    )
  }

  if (isOffice) {
    return (
      <iframe
        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewFile)}`}
        style={{ width: '100%', height: '90vh', border: 'none' }}
        title='Office File Preview'
        onLoad={onLoad}
      />
    )
  }

  return (
    <div className='text-center text-gray-500 p-4'>
      Cannot preview this file type.
    </div>
  )
}
